import { ref, readonly } from 'vue'

interface StatesValue {
  current_page: number
  current_position: number
  current_primary_key: string | number | null
  current_item_list: any[]
}

interface RememberParams {
  new_item_list: any[]
  primary_key_name: string
  last_page: number
  callback?: () => void
}

export const useHighlightRow = () => {
  const currentPage = ref(0)
  const currentPosition = ref(0)
  const currentPrimaryKey = ref<string | number | null>(null)
  const currentItemList = ref<any[]>([])

  const setCurrentPage = (value: number) => {
    currentPage.value = value
  }

  const setCurrentPosition = (value: number) => {
    currentPosition.value = value
  }

  const setCurrentPrimaryKey = (value: string | number | null) => {
    currentPrimaryKey.value = value
  }

  const setCurrentItemList = (value: any[]) => {
    currentItemList.value = value
  }

  const clearAll = () => {
    currentPage.value = 0
    currentPosition.value = 0
    currentPrimaryKey.value = null
    currentItemList.value = []
  }

  const remember = (params: RememberParams): number | void => {
    const { new_item_list, primary_key_name, last_page, callback } = params

    if (currentPage.value > last_page) {
      if (callback) {
        return callback()
      }
      return
    }

    if (
      !currentItemList.value ||
      currentPosition.value < 0 ||
      currentPosition.value >= currentItemList.value.length
    ) {
      return 0
    }

    const current_item = currentItemList.value[currentPosition.value]
    if (!currentPrimaryKey.value) {
      return currentPosition.value
    }
    const current_primary_key_value = current_item?.[currentPrimaryKey.value]

    if (current_primary_key_value === undefined) {
      return currentPosition.value
    }

    const item_index = new_item_list.findIndex(
      (item) => item[primary_key_name] === current_primary_key_value
    )

    if (item_index >= 0) {
      return item_index
    }

    const above_item = currentItemList.value[currentPosition.value - 1]
    const below_item = currentItemList.value[currentPosition.value + 1]

    const new_item_obj: Record<string | number, number> = {}
    new_item_list.forEach((item, idx) => {
      new_item_obj[item[primary_key_name]] = idx
    })

    if (below_item) {
      const belowOrder = new_item_obj[below_item[primary_key_name]]
      if (belowOrder !== undefined) {
        return belowOrder
      }
    }

    if (above_item) {
      const aboveOrder = new_item_obj[above_item[primary_key_name]]
      if (aboveOrder !== undefined) {
        return aboveOrder
      }
    }
    if (new_item_list.length > currentPosition.value)
      return currentPosition.value
    else return new_item_list.length - 1
  }

  const reSelect = (states_value: StatesValue) => {
    currentPage.value = states_value.current_page
    currentPosition.value = states_value.current_position
    currentPrimaryKey.value = states_value.current_primary_key
    currentItemList.value = states_value.current_item_list
  }

  return {
    currentPage: readonly(currentPage),
    currentPosition: readonly(currentPosition),
    currentPrimaryKey: readonly(currentPrimaryKey),
    currentItemList: readonly(currentItemList),
    setCurrentPage,
    setCurrentPosition,
    setCurrentPrimaryKey,
    setCurrentItemList,
    clearAll,
    remember,
    reSelect
  }
}
